// ============================================================================
//  CatalogueEntryDialog — what this code really is, and fixing it at the root
// ----------------------------------------------------------------------------
//  Opened from the ⓘ on any row of the billed-content filter. The officer is
//  looking at "M79.1 MYALGIA 331" and can tell something is wrong; this is where
//  they see everything the database holds about it and correct it once.
//
//  THREE THINGS ON SCREEN, IN THIS ORDER, AND THE ORDER IS THE POINT
//
//    1. IN THE CATALOGUE — the reference row(s) the import writes from. Editable.
//       Plural: I10 has four rows, one per G-DRG it can be billed under, and A01
//       has ten. A dialog showing "the" row would silently edit one of ten.
//
//    2. AS WRITTEN ON CLAIMS — every distinct spelling, with how many rows use
//       it. This is the evidence. 148 ICD-10 codes on this database are written
//       under more than one name; OBGY32A carries 38.
//
//    3. WHAT TO CHANGE ON THE CLAIMS ALREADY WRITTEN — opt-in, and off by
//       default. See the warning below; this is the part that can destroy data.
//
//  WHY THE CLAIM-SIDE EDITS ARE OFF BY DEFAULT
//
//  Because "38 spellings of one code" is not always 38 spellings of one thing.
//  Measured on OBGY32A: the names are "CAESARIAN SECTION", "POST-TERM PREGNANCY",
//  "FAILED INDUCTION OF LABOR" — the description column on a procedure row holds
//  the INDICATION, not the procedure, so collapsing them to one name would throw
//  away why each operation was done. On PARACETA1 the same control is exactly
//  right: two spellings of one drug over 6,148 rows.
//
//  Only the officer can tell those apart, so the dialog shows every name it would
//  overwrite and the count for each, and will not tick the box for them.
//
//  NOTHING IS WRITTEN WITHOUT A PREVIEW
//
//  Preview asks the server what would happen and gets back exact counts plus a
//  fingerprint. The confirmation quotes those counts, and the apply call sends the
//  fingerprint back — the server refuses it if the figures have moved since. So
//  the numbers agreed to are the numbers acted on, even if an import lands in
//  between.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Info, AlertTriangle, Loader2, Save, Database, FileText } from 'lucide-react';
import {
  claimStoreCatalogueEntry,
  claimStoreCatalogueEntryPreview,
  claimStoreCatalogueEntryRepair,
} from '../BaseURL';
import { confirmClaim, notifyClaim } from './claimsDialog';
import useDialogGuard from './dialogGuard';

const BRAND = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  head: '#f1f5f9', canvas: '#f8fafc',
  danger: '#b91c1c', dangerSoft: '#fef2f2',
  amber: '#92400e', amberSoft: '#fffbeb', amberLine: '#fde68a',
  navy: '#123A6D',
};
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const n0 = (v) => Number(v || 0).toLocaleString();

// ── One editable catalogue field ────────────────────────────────────────────
function Field({ label, value, onChange, readOnly, mono, wide }) {
  return (
    <label style={{
      display: 'grid', gap: 3, minWidth: 0,
      gridColumn: wide ? '1 / -1' : 'auto',
    }}>
      <span style={{
        fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
        textTransform: 'uppercase', color: BRAND.inkFaint,
      }}>{label}</span>
      <input
        value={value == null ? '' : String(value)}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        readOnly={readOnly}
        aria-label={label}
        style={{
          padding: '6px 8px', fontSize: 12, borderRadius: 6, minWidth: 0,
          fontFamily: mono ? MONO : 'inherit',
          border: `1px solid ${BRAND.line}`,
          background: readOnly ? BRAND.canvas : '#fff',
          color: readOnly ? BRAND.inkMuted || BRAND.inkSoft : BRAND.ink,
          cursor: readOnly ? 'default' : 'text',
        }} />
    </label>
  );
}

export default function CatalogueEntryDialog({
  kind, code, name, scope, onClose, onRepaired,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  //  Catalogue edits, keyed `table#id`, holding only the columns actually typed
  //  in. Only what MOVED is ever sent — the server ignores a field posted back
  //  unchanged, but sending the whole row would make every preview read as nine
  //  changes when one was made.
  const [edits, setEdits] = useState({});

  //  The claim-side opt-ins. Both off to start. See the header.
  const [pushCode, setPushCode] = useState(false);
  const [pushName, setPushName] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [claimName, setClaimName] = useState('');
  const [allClaims, setAllClaims] = useState(false);
  //  This one is nothing but typed edits held in `edits` until Apply — the worst
  //  possible thing to lose to a mis-aimed click.
  const { panelRef, backdropProps } = useDialogGuard();

  const load = useCallback(() => {
    setError(null);
    setData(null);
    axios.get(claimStoreCatalogueEntry, { params: { kind, code, name, ...(scope || {}) } })
      .then(({ data: d }) => {
        setData(d);
        setClaimCode(d?.code || '');
        //  Pre-filled with the spelling the most rows already use, so "normalise
        //  to the common one" is the default typing rather than a decision.
        setClaimName(d?.usage?.spellings?.[0]?.name || '');
      })
      .catch((e) => setError(e?.response?.data?.message || e.message));
  }, [kind, code, name, scope]);

  useEffect(load, [load]);

  const setField = (table, id, column, value) => setEdits((prev) => {
    const key = `${table}#${id}`;
    return { ...prev, [key]: { ...(prev[key] || {}), [column]: value } };
  });

  //  What will be sent. Built fresh each render from `edits` so a field typed and
  //  then typed back to its original value drops out on its own.
  const catalogueEdits = useMemo(() => {
    const out = [];
    for (const group of data?.catalogue || []) {
      for (const row of group.rows) {
        const key = `${group.table}#${row[group.idColumn]}`;
        const typed = edits[key];
        if (!typed) continue;
        const changes = {};
        for (const [col, v] of Object.entries(typed)) {
          if (String(row[col] == null ? '' : row[col]) !== String(v)) changes[col] = v;
        }
        if (Object.keys(changes).length) {
          out.push({ table: group.table, id: row[group.idColumn], changes });
        }
      }
    }
    return out;
  }, [edits, data]);

  const claimEdits = useMemo(() => {
    const out = {};
    if (pushCode && claimCode.trim()) out.code = claimCode.trim();
    if (pushName) out.name = claimName.trim();
    return out;
  }, [pushCode, pushName, claimCode, claimName]);

  const target = useMemo(() => {
    const hasCat = catalogueEdits.length > 0;
    const hasClaims = Object.keys(claimEdits).length > 0;
    if (hasCat && hasClaims) return 'both';
    if (hasClaims) return 'claims';
    return 'catalogue';
  }, [catalogueEdits, claimEdits]);

  const nothingToDo = catalogueEdits.length === 0
    && Object.keys(claimEdits).length === 0;

  //  The spellings that `claimName` would overwrite, computed here so the warning
  //  is on screen BEFORE Preview is pressed rather than only in the confirmation.
  const wouldOverwrite = useMemo(() => {
    if (!pushName || !data) return [];
    const keep = claimName.trim().toLowerCase();
    return (data.usage.spellings || [])
      .filter((s) => String(s.name || '').trim().toLowerCase() !== keep);
  }, [pushName, claimName, data]);

  // ── Preview, confirm, apply ───────────────────────────────────────────────
  const run = async () => {
    if (nothingToDo) return;
    setBusy(true);
    setError(null);
    try {
      const body = { kind, code: data.code, catalogueEdits, claimEdits, target,
        allClaims, ...(scope || {}) };
      const { data: plan } = await axios.post(claimStoreCatalogueEntryPreview, body);

      if (plan.empty) {
        await notifyClaim({
          title: 'Nothing would change',
          lead: 'Every value is already what you asked for.',
        });
        return;
      }

      // ── The question the officer actually has to answer ─────────────────
      const items = [];
      for (const c of plan.catalogue || []) {
        for (const [col, mv] of Object.entries(c.changes)) {
          items.push({
            text: `${c.table}: ${col} "${mv.from ?? ''}" becomes "${mv.to ?? ''}"`,
          });
        }
        if (c.collision) items.push({ text: c.collision, warn: true });
      }
      if (plan.claims) {
        const where = plan.claims.scoped
          ? 'in the month on screen' : 'in every month';
        if (plan.claims.codeMoves != null) {
          items.push({
            text: `${n0(plan.claims.codeMoves)} claim row(s) ${where} change code `
              + `to "${plan.claims.newCode}"`,
            warn: plan.claims.codeMoves > 0,
          });
        }
        if (plan.claims.nameMoves != null) {
          items.push({
            text: `${n0(plan.claims.nameMoves)} claim row(s) ${where} are renamed `
              + `to "${plan.claims.newName}"`,
            warn: plan.claims.nameMoves > 0,
          });
          //  Named, not counted. This is the part that cannot be undone, and the
          //  reason it is opt-in: on some codes these are different clinical
          //  indications rather than misspellings of one name.
          for (const r of (plan.claims.replacing || []).slice(0, 8)) {
            items.push({ text: `  overwrites ${n0(r.rows)} × "${r.name}"`, warn: true });
          }
          if ((plan.claims.replacing || []).length > 8) {
            items.push({
              text: `  …and ${(plan.claims.replacing.length - 8)} more spelling(s)`,
              warn: true,
            });
          }
        }
        items.push({
          text: `${n0(plan.claims.matched.claims)} claim(s) are affected in total`,
        });
      }
      for (const p of plan.problems || []) items.push({ text: p, warn: true });

      const ok = await confirmClaim({
        title: plan.claims
          ? `Apply this to ${n0(plan.claims.matched.claims)} claim(s)?`
          : 'Update the catalogue?',
        lead: plan.claims
          ? 'The catalogue is corrected so future claims are right, and the claims '
            + 'already written are corrected too.'
          : 'Only the catalogue changes. Claims already written keep what they have.',
        items,
        note: 'This cannot be undone from here. Every change is recorded in the '
          + 'activity log.',
        confirmText: 'Apply',
        tone: (plan.problems || []).length || (plan.claims && plan.claims.nameMoves)
          ? 'danger' : 'primary',
      });
      if (!ok) return;

      const { data: done } = await axios.post(claimStoreCatalogueEntryRepair,
        { ...body, fingerprint: plan.fingerprint });

      await notifyClaim({
        title: 'Done',
        lead: done.message,
        note: 'The lists on this screen still show what was loaded before the '
          + 'change — re-run the search to see it.',
      });
      setEdits({});
      setPushCode(false);
      setPushName(false);
      load();
      if (onRepaired) onRepaired(done);
    } catch (e) {
      const res = e?.response;
      //  409 means the preview went stale. Reload so the officer sees the current
      //  figures rather than a message about numbers they can no longer check.
      if (res?.status === 409) {
        setError(res.data?.message || 'The figures changed — check again.');
        load();
      } else {
        setError(res?.data?.message || e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const catalogueRowCount = (data?.catalogue || [])
    .reduce((a, g) => a + g.rows.length, 0);

  return (
    <div
      role="dialog" aria-modal="true"
      aria-label={`${data?.label || kind} ${code}`}
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 1400,
        background: 'rgba(15,23,42,.46)',
        //  `overflow: auto` with `margin: auto` on the child, NOT
        //  `align-items: center` — a flex container cannot scroll to content
        //  centring has pushed above its top edge, which is how a tall dialog
        //  loses its own header.
        display: 'flex', overflow: 'auto', padding: '24px 16px',
      }}>
      <div
        ref={panelRef}
        style={{
          margin: 'auto', width: '100%', maxWidth: 720,
          background: '#fff', borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(15,23,42,.30)',
          border: `1px solid ${BRAND.line}`,
        }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 16px', borderBottom: `1px solid ${BRAND.line}`,
          background: BRAND.canvas,
        }}>
          <Info size={16} style={{ color: BRAND.navy, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.ink }}>
              {data?.label || kind}
              <span style={{ fontFamily: MONO, marginLeft: 8, color: BRAND.navy }}>
                {code}
              </span>
            </div>
            {data && (
              <div style={{ fontSize: 11.5, color: BRAND.inkSoft, marginTop: 1 }}>
                {n0(data.usage.rows)} row(s) on {n0(data.usage.claims)} claim(s)
                {data.usage.inScope && <> · {n0(data.usage.inScope.rows)} in the
                  month on screen</>}
                {' · '}{catalogueRowCount} catalogue row(s)
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{
              border: 0, background: 'none', cursor: 'pointer', padding: 4,
              color: BRAND.inkFaint, lineHeight: 0,
            }}>
            <X size={17} />
          </button>
        </div>

        <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {error && (
            <div style={{
              margin: 12, padding: '9px 11px', borderRadius: 8, fontSize: 12,
              background: BRAND.dangerSoft, color: BRAND.danger,
              border: `1px solid ${BRAND.danger}33`,
            }}>{error}</div>
          )}

          {!data && !error && (
            <div style={{
              padding: 28, textAlign: 'center', fontSize: 12.5, color: BRAND.inkFaint,
            }}>
              <Loader2 size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
              Reading what the database holds…
            </div>
          )}

          {data && (
            <div style={{ padding: 14, display: 'grid', gap: 14 }}>

              {/* ── 1. THE CATALOGUE ──────────────────────────────────────── */}
              <section>
                <SectionLabel icon={Database}>In the catalogue</SectionLabel>

                {data.orphan ? (
                  <div style={{
                    padding: '10px 12px', borderRadius: 8, fontSize: 12,
                    lineHeight: 1.6, background: BRAND.amberSoft,
                    color: BRAND.amber, border: `1px solid ${BRAND.amberLine}`,
                  }}>
                    <b>This code is not in the catalogue at all.</b> It is on
                    {' '}{n0(data.usage.claims)} claim(s) but no reference row exists, so
                    there is nothing here to correct at the root — a future import
                    will not produce it either. Fix the claims below if the value is
                    wrong, and add the entry through <b>NHIS Catalogue Import</b> if
                    it should exist.
                  </div>
                ) : data.catalogue.map((group) => (
                  <div key={group.table} style={{ display: 'grid', gap: 8 }}>
                    {group.error && (
                      <div style={{ fontSize: 11.5, color: BRAND.danger }}>
                        {group.table} could not be read: {group.error}
                      </div>
                    )}
                    {group.rows.map((row) => {
                      const id = row[group.idColumn];
                      const touched = edits[`${group.table}#${id}`];
                      return (
                        <div key={`${group.table}-${id}`} style={{
                          border: `1px solid ${touched ? BRAND.green : BRAND.line}`,
                          borderRadius: 9, padding: 10,
                          background: touched ? BRAND.greenSoft : '#fff',
                        }}>
                          <div style={{
                            display: 'flex', gap: 8, alignItems: 'baseline',
                            marginBottom: 8, flexWrap: 'wrap',
                          }}>
                            <span style={{
                              fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
                              textTransform: 'uppercase', color: BRAND.inkFaint,
                            }}>{group.table} #{id}</span>
                            {group.readOnly.map((c) => (
                              <span key={c} style={{ fontSize: 11, color: BRAND.inkSoft }}>
                                {group.labels[c] || c}:{' '}
                                <b style={{ color: BRAND.ink }}>{String(row[c] ?? '—')}</b>
                              </span>
                            ))}
                          </div>
                          <div style={{
                            display: 'grid', gap: 8,
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          }}>
                            {group.editable.map((col) => (
                              <Field
                                key={col}
                                label={group.labels[col] || col}
                                value={touched && col in touched ? touched[col] : row[col]}
                                onChange={(v) => setField(group.table, id, col, v)}
                                mono={col === group.codeColumn || /code|icd|drg/i.test(col)}
                                wide={col === group.nameColumn}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </section>

              {/* ── 2. WHAT THE CLAIMS SAY ────────────────────────────────── */}
              <section>
                <SectionLabel icon={FileText}>
                  As written on claims
                  {data.usage.spellings.length > 1
                    && ` · ${data.usage.spellings.length} different names`}
                </SectionLabel>
                <div style={{
                  border: `1px solid ${BRAND.line}`, borderRadius: 9,
                  maxHeight: 150, overflowY: 'auto',
                }}>
                  {data.usage.spellings.map((s, i) => (
                    <div key={`${s.name}-${i}`} style={{
                      display: 'flex', gap: 10, alignItems: 'baseline',
                      padding: '6px 10px', fontSize: 12,
                      borderBottom: i === data.usage.spellings.length - 1
                        ? 'none' : `1px solid ${BRAND.head}`,
                    }}>
                      <span style={{
                        fontWeight: 800, color: BRAND.inkSoft, minWidth: 54,
                        textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                      }}>{n0(s.rows)}</span>
                      <span style={{ color: BRAND.ink, minWidth: 0, wordBreak: 'break-word' }}>
                        {s.name || <i style={{ color: BRAND.inkFaint }}>(blank)</i>}
                      </span>
                    </div>
                  ))}
                </div>
                {data.usage.spellings.length > 1 && (
                  <div style={{ fontSize: 11, color: BRAND.inkFaint, marginTop: 5 }}>
                    More than one name for one code. Sometimes that is a spelling to
                    tidy; on a procedure it is often the clinical indication, which is
                    different information on every row and should be left alone.
                  </div>
                )}
              </section>

              {/* ── 3. THE CLAIMS ALREADY WRITTEN ─────────────────────────── */}
              <section>
                <SectionLabel icon={AlertTriangle}>
                  Also change the claims already written
                </SectionLabel>
                <div style={{
                  border: `1px solid ${BRAND.line}`, borderRadius: 9, padding: 10,
                  display: 'grid', gap: 9,
                }}>
                  <Opt
                    on={pushCode} onToggle={() => setPushCode((v) => !v)}
                    label={`Set the ${data.codeLabel.toLowerCase()} on every claim row to`}>
                    <input
                      value={claimCode}
                      onChange={(e) => setClaimCode(e.target.value)}
                      disabled={!pushCode}
                      aria-label="New code for claim rows"
                      style={{
                        fontFamily: MONO, fontSize: 12, padding: '5px 8px',
                        borderRadius: 6, border: `1px solid ${BRAND.line}`,
                        width: 150, background: pushCode ? '#fff' : BRAND.canvas,
                      }} />
                  </Opt>

                  <Opt
                    on={pushName} onToggle={() => setPushName((v) => !v)}
                    label="Set the name on every claim row to">
                    <input
                      value={claimName}
                      onChange={(e) => setClaimName(e.target.value)}
                      disabled={!pushName}
                      aria-label="New name for claim rows"
                      style={{
                        fontSize: 12, padding: '5px 8px', borderRadius: 6,
                        border: `1px solid ${BRAND.line}`, flex: 1, minWidth: 140,
                        background: pushName ? '#fff' : BRAND.canvas,
                      }} />
                  </Opt>

                  {wouldOverwrite.length > 0 && (
                    <div style={{
                      padding: '8px 10px', borderRadius: 7, fontSize: 11.5,
                      lineHeight: 1.55, background: BRAND.amberSoft,
                      color: BRAND.amber, border: `1px solid ${BRAND.amberLine}`,
                    }}>
                      <b>This overwrites {wouldOverwrite.length} other name
                      {wouldOverwrite.length === 1 ? '' : 's'}</b> on
                      {' '}{n0(wouldOverwrite.reduce((a, s) => a + s.rows, 0))} row(s):
                      {' '}{wouldOverwrite.slice(0, 4).map((s) => `"${s.name}"`).join(', ')}
                      {wouldOverwrite.length > 4 && ` and ${wouldOverwrite.length - 4} more`}.
                      {' '}Check they are the same thing said differently.
                    </div>
                  )}

                  {/*  Narrower by default. "Every claim" has to be chosen. */}
                  <div style={{
                    display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
                    paddingTop: 3, borderTop: `1px solid ${BRAND.head}`,
                    opacity: pushCode || pushName ? 1 : .5,
                  }}>
                    <span style={{
                      fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
                      textTransform: 'uppercase', color: BRAND.inkFaint,
                    }}>Apply to</span>
                    {[[false, 'The month on screen'], [true, 'Every claim, every month']]
                      .map(([v, lbl]) => (
                        <label key={String(v)} style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontSize: 12, color: BRAND.ink,
                          cursor: pushCode || pushName ? 'pointer' : 'default',
                        }}>
                          <input
                            type="radio" name="entry-scope"
                            checked={allClaims === v}
                            disabled={!pushCode && !pushName}
                            onChange={() => setAllClaims(v)} />
                          {lbl}
                          {v === false && data.usage.inScope && (
                            <span style={{ color: BRAND.inkFaint }}>
                              ({n0(data.usage.inScope.rows)} rows)
                            </span>
                          )}
                          {v === true && (
                            <span style={{ color: BRAND.inkFaint }}>
                              ({n0(data.usage.rows)} rows)
                            </span>
                          )}
                        </label>
                      ))}
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          padding: '11px 14px', borderTop: `1px solid ${BRAND.line}`,
          background: BRAND.canvas,
        }}>
          <span style={{ flex: 1, fontSize: 11.5, color: BRAND.inkFaint }}>
            {nothingToDo
              ? 'Edit a field above, or tick a box, to see what it would change.'
              : `${catalogueEdits.length} catalogue row(s)`
                + (Object.keys(claimEdits).length ? ' and the claim rows' : '')
                + ' would change — you will see the exact counts first.'}
          </span>
          <button type="button" onClick={onClose}
            style={{
              padding: '8px 15px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
              border: `1px solid ${BRAND.line}`, background: '#fff',
              color: BRAND.inkSoft, cursor: 'pointer', font: 'inherit',
            }}>
            Close
          </button>
          <button type="button" onClick={run} disabled={nothingToDo || busy}
            style={{
              padding: '8px 15px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
              border: 0, cursor: nothingToDo || busy ? 'default' : 'pointer',
              background: nothingToDo || busy ? BRAND.inkFaint : BRAND.green,
              color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            {busy ? <Loader2 size={14} /> : <Save size={14} />}
            {busy ? 'Checking…' : 'Preview the change'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7,
    }}>
      {Icon && <Icon size={12} style={{ color: BRAND.inkFaint, flexShrink: 0 }} />}
      <span style={{
        fontSize: 10, fontWeight: 800, letterSpacing: '.06em',
        textTransform: 'uppercase', color: BRAND.inkSoft,
      }}>{children}</span>
    </div>
  );
}

//  A tick box with its own control beside it. The control is disabled rather than
//  hidden when the box is off, so the shape of the form does not jump as boxes are
//  ticked — and so the value can be read before deciding to apply it.
function Opt({ on, onToggle, label, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
        color: on ? BRAND.ink : BRAND.inkSoft, cursor: 'pointer', fontWeight: on ? 700 : 500,
      }}>
        <input type="checkbox" checked={on} onChange={onToggle} />
        {label}
      </label>
      {children}
    </div>
  );
}
